/**
 * Soft-superseded proof/production docs stay on the order for preview/history.
 * Submit Proof must only treat non-superseded rows as the editable "current" set.
 */

/**
 * Exhaustive list of OrderResponse / order-payload keys that Submit Proof's Attachments
 * bind as *existing* document arrays.
 *
 * Matches `OrderResponse` JSON:
 * - `proof_documents`
 * - `production_documents`
 * - `product_proof_documents`
 *
 * Deliberately excluded (not soft-superseded by the revision clear-down, or not editable
 * existing-sets on this screen):
 * - `new_proof_documents` / `new_production_documents` / `new_product_proof_documents`
 *   (session uploads — never carry `is_superseded` from the backend)
 * - `revision_documents`, `uploaded_file` / `new_documents`, drawings
 *   (different attachment categories)
 * - `order_details[].proof_documents` (line-level RFQ/order UI — not VAS Submit Proof clear-down)
 */
export const SUBMIT_PROOF_EXISTING_DOC_FIELDS = Object.freeze([
  "proof_documents",
  "production_documents",
  "product_proof_documents",
]);

export function isSupersededDocument(doc) {
  return doc?.is_superseded === true || doc?.isSuperseded === true;
}

export function activeDocuments(docs) {
  if (!Array.isArray(docs)) return [];
  return docs.filter((doc) => !isSupersededDocument(doc));
}

export function supersededDocuments(docs) {
  if (!Array.isArray(docs)) return [];
  return docs.filter((doc) => isSupersededDocument(doc));
}

/**
 * PGSS-5267: Revision Histories filter for staff declutter.
 *
 * Current-state APIs already omit superseded rows for customers/suppliers/staff.
 * History returns them flagged because a round is a point-in-time record — and
 * `is_superseded` means "superseded *now*", not "was superseded when this round
 * was sent". The page defaults to hiding (declutter); turn the toggle off to see
 * the full round record with badges. Hiding can also drop a file that was current
 * at send time — that tradeoff is intentional product choice for this screen.
 */
export function documentsForRevisionHistory(docs, { hideSuperseded = true } = {}) {
  if (!Array.isArray(docs)) return [];
  if (hideSuperseded) {
    return activeDocuments(docs);
  }
  return docs;
}

/** Strip superseded rows from every Submit Proof existing-doc field (see list above). */
export function withoutSupersededProofFields(orderPayload) {
  if (!orderPayload) return orderPayload;
  const next = { ...orderPayload };
  for (const field of SUBMIT_PROOF_EXISTING_DOC_FIELDS) {
    next[field] = activeDocuments(orderPayload[field]);
  }
  return next;
}
